import React, { useState } from 'react';

const NewAbout = () => {
  const [primaryHover, setPrimaryHover] = useState(false);
  const [secondaryHover, setSecondaryHover] = useState(false);
  const [consultHover, setConsultHover] = useState(false);

  const colors = {
    white: '#ffffff',
    softWhite: '#f8fbff',
    ice: '#eef8ff',
    iceBlue: '#e0f2fe',
    blue: '#0284c7',
    blueDark: '#0369a1',
    cyan: '#06b6d4',
    navy: '#0f172a',
    slate: '#475569',
    lightSlate: '#64748b',
    border: '#dbeafe',
    borderDark: '#cbd5e1',
  };

  const styles = {
    outer: {
      width: '100%',
      minHeight: 'calc(100vh - 94px)',
      background: colors.softWhite,
      fontFamily: '"Inter", system-ui, -apple-system, sans-serif',
      overflow: 'hidden',
      position: 'relative',
    },

    hero: {
      position: 'relative',
      minHeight: 'calc(100vh - 94px)',
      padding: '55px 5% 70px',
      display: 'flex',
      alignItems: 'center',
      overflow: 'hidden',
      boxSizing: 'border-box',
    },

    grid: {
      position: 'absolute',
      inset: 0,
      backgroundImage: `
        linear-gradient(rgba(2,132,199,0.035) 1px, transparent 1px),
        linear-gradient(90deg, rgba(2,132,199,0.035) 1px, transparent 1px)
      `,
      backgroundSize: '55px 55px',
      animation: 'gridMove 25s linear infinite',
      pointerEvents: 'none',
    },

    glowOne: {
      position: 'absolute',
      width: '500px',
      height: '500px',
      borderRadius: '50%',
      background: 'rgba(14,165,233,0.08)',
      filter: 'blur(80px)',
      top: '-220px',
      left: '-180px',
      animation: 'floatGlow 8s ease-in-out infinite',
      pointerEvents: 'none',
    },

    glowTwo: {
      position: 'absolute',
      width: '450px',
      height: '450px',
      borderRadius: '50%',
      background: 'rgba(6,182,212,0.08)',
      filter: 'blur(90px)',
      right: '-180px',
      bottom: '-180px',
      animation: 'floatGlowReverse 10s ease-in-out infinite',
      pointerEvents: 'none',
    },

    container: {
      position: 'relative',
      zIndex: 3,
      width: '100%',
      maxWidth: '1380px',
      margin: '0 auto',
    },

    heroGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 0.95fr',
      alignItems: 'center',
      gap: '70px',
    },

    left: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      animation: 'heroContentIn 0.9s ease-out both',
    },

    eyebrow: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '9px',
      padding: '8px 15px',
      borderRadius: '999px',
      background: 'rgba(224,242,254,0.85)',
      border: `1px solid ${colors.border}`,
      color: colors.blueDark,
      fontSize: '0.76rem',
      fontWeight: '800',
      letterSpacing: '0.13em',
      textTransform: 'uppercase',
      marginBottom: '22px',
      boxShadow: '0 8px 25px rgba(2,132,199,0.06)',
    },

    liveDot: {
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      background: colors.cyan,
      boxShadow: `0 0 0 5px rgba(6,182,212,0.12)`,
      animation: 'pulseDot 2s infinite',
    },

    heading: {
      margin: 0,
      color: colors.navy,
      fontSize: 'clamp(2.9rem, 5.3vw, 5.4rem)',
      lineHeight: '0.99',
      letterSpacing: '-0.055em',
      fontWeight: '850',
      maxWidth: '720px',
    },

    headingBlue: {
      color: colors.blue,
      position: 'relative',
      display: 'inline-block',
    },

    headingLine: {
      height: '9px',
      position: 'absolute',
      left: '2%',
      right: '2%',
      bottom: '-2px',
      borderRadius: '20px',
      background: 'linear-gradient(90deg, #bae6fd, #67e8f9)',
      zIndex: -1,
      animation: 'lineExpand 1.2s ease-out 0.8s both',
      transformOrigin: 'left',
    },

    description: {
      maxWidth: '650px',
      color: colors.slate,
      fontSize: '1.08rem',
      lineHeight: '1.75',
      margin: '25px 0 0',
    },

    buttonRow: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '14px',
      marginTop: '32px',
    },

    primaryButton: {
      border: 'none',
      background: `linear-gradient(135deg, ${colors.blue}, ${colors.blueDark})`,
      color: '#ffffff',
      padding: '14px 23px',
      borderRadius: '11px',
      fontWeight: '750',
      fontSize: '0.95rem',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '9px',
      boxShadow: '0 12px 28px rgba(2,132,199,0.2)',
      transform: primaryHover ? 'translateY(-3px)' : 'translateY(0)',
      transition: 'all 0.3s ease',
    },

    secondaryButton: {
      border: `1px solid ${colors.borderDark}`,
      background: '#ffffff',
      color: colors.navy,
      padding: '14px 23px',
      borderRadius: '11px',
      fontWeight: '700',
      fontSize: '0.95rem',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '9px',
      transform: secondaryHover ? 'translateY(-3px)' : 'translateY(0)',
      boxShadow: secondaryHover
        ? '0 12px 25px rgba(15,23,42,0.08)'
        : '0 4px 10px rgba(15,23,42,0.03)',
      transition: 'all 0.3s ease',
    },

    trustRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '22px',
      marginTop: '36px',
      flexWrap: 'wrap',
    },

    trustItem: {
      display: 'flex',
      flexDirection: 'column',
      gap: '3px',
    },

    trustNumber: {
      fontSize: '1.25rem',
      fontWeight: '850',
      color: colors.navy,
    },

    trustLabel: {
      fontSize: '0.75rem',
      color: colors.lightSlate,
      fontWeight: '600',
    },

    divider: {
      height: '32px',
      width: '1px',
      background: colors.borderDark,
    },

    visualArea: {
      position: 'relative',
      minHeight: '590px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      animation: 'visualReveal 1s ease-out 0.15s both',
    },

    imageFrame: {
      width: '100%',
      maxWidth: '610px',
      height: '510px',
      borderRadius: '28px',
      overflow: 'hidden',
      position: 'relative',
      border: '9px solid rgba(255,255,255,0.9)',
      boxShadow:
        '0 35px 70px rgba(15,23,42,0.15), 0 8px 25px rgba(2,132,199,0.08)',
      transform: 'rotate(1deg)',
    },

    image: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      display: 'block',
      filter: 'saturate(0.9) contrast(1.03)',
    },

    imageOverlay: {
      position: 'absolute',
      inset: 0,
      background:
        'linear-gradient(135deg, rgba(2,132,199,0.18), transparent 45%, rgba(15,23,42,0.15))',
      pointerEvents: 'none',
    },

    technicalLines: {
      position: 'absolute',
      inset: '20px',
      border: '1px solid rgba(255,255,255,0.35)',
      borderRadius: '20px',
      pointerEvents: 'none',
    },

    floatingCard: {
      position: 'absolute',
      background: 'rgba(255,255,255,0.94)',
      backdropFilter: 'blur(15px)',
      border: `1px solid rgba(255,255,255,0.9)`,
      borderRadius: '15px',
      boxShadow: '0 18px 40px rgba(15,23,42,0.12)',
      padding: '15px 18px',
      zIndex: 5,
      animation: 'floatingCard 5s ease-in-out infinite',
    },

    floatingTop: {
      top: '42px',
      left: '-28px',
    },

    floatingBottom: {
      bottom: '45px',
      right: '-28px',
      animationDelay: '1.5s',
    },

    floatingTitle: {
      fontSize: '0.72rem',
      color: colors.lightSlate,
      fontWeight: '700',
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      marginBottom: '5px',
    },

    floatingValue: {
      fontSize: '1.1rem',
      fontWeight: '850',
      color: colors.navy,
    },

    floatingBlue: {
      color: colors.blue,
    },

    cornerBadge: {
      position: 'absolute',
      right: '35px',
      top: '18px',
      zIndex: 6,
      background: colors.navy,
      color: '#ffffff',
      borderRadius: '10px',
      padding: '9px 13px',
      fontSize: '0.72rem',
      fontWeight: '700',
      letterSpacing: '0.06em',
      boxShadow: '0 10px 25px rgba(15,23,42,0.2)',
    },

    servicesStrip: {
      marginTop: '65px',
      padding: '20px',
      borderRadius: '18px',
      border: `1px solid ${colors.border}`,
      background: 'rgba(255,255,255,0.72)',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',

      display: 'grid',
      gridTemplateColumns: 'repeat(5, 1fr)',

      gap: '12px',

      boxShadow:
        '0 12px 35px rgba(15,23,42,0.04)',

      animation:
        'stripIn 1s ease-out 0.6s both',
    },

    stripItem: {
      padding: '15px 18px',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      transition: 'all 0.3s ease',
    },

    stripIcon: {
      width: '38px',
      height: '38px',
      flexShrink: 0,
      borderRadius: '10px',
      background: colors.iceBlue,
      color: colors.blue,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: '800',
      fontSize: '0.85rem',
    },

    stripTitle: {
      color: colors.navy,
      fontWeight: '750',
      fontSize: '0.84rem',
      margin: 0,
    },

    stripText: {
      color: colors.lightSlate,
      fontSize: '0.72rem',
      margin: '3px 0 0',
    },

    scrollIndicator: {
      position: 'absolute',
      bottom: '18px',
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: 5,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '7px',
      color: colors.lightSlate,
      fontSize: '0.68rem',
      fontWeight: '700',
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
    },

    scrollMouse: {
      width: '20px',
      height: '31px',
      border: `2px solid ${colors.borderDark}`,
      borderRadius: '20px',
      position: 'relative',
    },

    scrollDot: {
      position: 'absolute',
      width: '4px',
      height: '7px',
      borderRadius: '5px',
      background: colors.blue,
      top: '5px',
      left: '50%',
      transform: 'translateX(-50%)',
      animation: 'scrollDot 1.7s infinite',
    },
    engineeringGrid: {
      position: 'absolute',
      inset: 0,
      backgroundImage: `
        linear-gradient(
          rgba(8,121,209,0.045) 1px,
          transparent 1px
        ),
        linear-gradient(
          90deg,
          rgba(8,121,209,0.045) 1px,
          transparent 1px
        )
      `,
      backgroundSize: '48px 48px',
      animation: 'aboutGridMove 24s linear infinite',
      pointerEvents: 'none',
      zIndex: 0,
    },
    dotGrid: {
      position: 'absolute',
      inset: 0,
      backgroundImage:
        'radial-gradient(rgba(8,121,209,0.17) 1px, transparent 1px)',
      backgroundSize: '25px 25px',
      opacity: 0.25,
      animation: 'aboutDotMove 18s linear infinite',
      pointerEvents: 'none',
      zIndex: 0,
    },
  };

  return (
    <section style={styles.outer}>
      <style>{`
        @keyframes gridMove {
          0% { background-position: 0 0, 0 0; }
          100% { background-position: 55px 55px, 55px 55px; }
        }

        @keyframes floatGlow {
          0%,100% { transform: translate(0,0) scale(1); }
          50% { transform: translate(35px,25px) scale(1.08); }
        }

        @keyframes floatGlowReverse {
          0%,100% { transform: translate(0,0) scale(1); }
          50% { transform: translate(-30px,-20px) scale(1.08); }
        }

        @keyframes heroContentIn {
          0% {
            opacity: 0;
            transform: translateX(-35px);
          }
          100% {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes visualReveal {
          0% {
            opacity: 0;
            transform: translateX(40px) scale(0.96);
          }
          100% {
            opacity: 1;
            transform: translateX(0) scale(1);
          }
        }

        @keyframes lineExpand {
          0% {
            transform: scaleX(0);
          }
          100% {
            transform: scaleX(1);
          }
        }

        @keyframes pulseDot {
          0%,100% {
            box-shadow: 0 0 0 4px rgba(6,182,212,0.10);
          }
          50% {
            box-shadow: 0 0 0 8px rgba(6,182,212,0.02);
          }
        }

        @keyframes floatingCard {
          0%,100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }

        @keyframes stripIn {
          0% {
            opacity: 0;
            transform: translateY(25px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes scrollDot {
          0% {
            opacity: 0;
            transform: translate(-50%, 0);
          }
          30% {
            opacity: 1;
          }
          100% {
            opacity: 0;
            transform: translate(-50%, 12px);
          }
        }

        .landing-strip-item:hover {
          background: #ffffff;
          transform: translateY(-3px);
          box-shadow: 0 8px 20px rgba(15,23,42,0.06);
        }

        .landing-image {
          transition: transform 0.7s cubic-bezier(0.2,0.8,0.2,1);
        }

        .landing-image-frame:hover .landing-image {
          transform: scale(1.045);
        }

        @media (max-width: 1100px) {
          .landing-hero-grid {
            grid-template-columns: 1fr !important;
            gap: 35px !important;
          }

          .landing-left {
            align-items: center !important;
            text-align: center !important;
          }

          .landing-description {
            margin-left: auto !important;
            margin-right: auto !important;
          }

          .landing-visual {
            max-width: 700px !important;
            width: 100% !important;
            margin: 0 auto !important;
            min-height: 500px !important;
          }

          .landing-services-strip {
            grid-template-columns: repeat(3, 1fr) !important;
          }
        }

        @media (max-width: 700px) {
          .landing-hero {
            padding: 38px 20px 70px !important;
          }

          .landing-heading {
            font-size: clamp(2.45rem, 12vw, 4rem) !important;
          }

          .landing-description {
            font-size: 0.96rem !important;
          }

          .landing-visual {
            min-height: 390px !important;
          }

          .landing-image-frame {
            height: 350px !important;
            border-width: 6px !important;
            border-radius: 20px !important;
            transform: none !important;
          }

          .landing-floating-top {
            left: -5px !important;
            top: 20px !important;
          }

          .landing-floating-bottom {
            right: -5px !important;
            bottom: 20px !important;
          }

          .landing-corner-badge {
            right: 15px !important;
            top: 12px !important;
          }

          .landing-trust-row {
            justify-content: center !important;
          }

          .landing-button-row {
            width: 100% !important;
            justify-content: center !important;
          }

          .landing-primary-button,
          .landing-secondary-button {
            width: 100% !important;
            justify-content: center !important;
          }

          .landing-services-strip {
            grid-template-columns: 1fr !important;
            margin-top: 35px !important;
          }

          .landing-scroll {
            display: none !important;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          *,
          *::before,
          *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }
      `}</style>

      <div style={styles.engineeringGrid} />
      <div style={styles.dotGrid} />


      {/* <div style={styles.grid} /> */}
      {/* <div style={styles.glowOne} />
      <div style={styles.glowTwo} /> */}

      <div style={styles.hero} className="landing-hero">
        <div style={styles.container}>

          <div
            style={styles.heroGrid}
            className="landing-hero-grid"
          >

            {/* ================= LEFT CONTENT ================= */}

            <div style={styles.left} className="landing-left">

              <div style={styles.eyebrow}>
                <span style={styles.liveDot} />
                Pune Based Engineering Consultancy
              </div>

              <h1
                style={styles.heading}
                className="landing-heading"
              >
                Engineering
                <br />

                <span style={styles.headingBlue}>
                  Ideas Into Reality
                  <span style={styles.headingLine} />
                </span>

                <br />

                With Precision.
              </h1>

              <p
                style={styles.description}
                className="landing-description"
              >
                SANSA CONSULTANT INDIA LLP delivers structural engineering,
                advanced analysis, 3D detailing, Land Development and Land Survey
                solutions that transform complex engineering requirements into
                safe, practical and construction-ready results.
              </p>

              {/* Buttons */}

              <div
                style={styles.buttonRow}
                className="landing-button-row"
              >

                <button
                  style={styles.primaryButton}
                  className="landing-primary-button"
                  onMouseEnter={() => setPrimaryHover(true)}
                  onMouseLeave={() => setPrimaryHover(false)}
                  onClick={() => {
                    document
                      .getElementById('contact')
                      ?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  Start Your Project
                  <span>→</span>
                </button>

                <button
                  style={styles.secondaryButton}
                  className="landing-secondary-button"
                  onMouseEnter={() => setSecondaryHover(true)}
                  onMouseLeave={() => setSecondaryHover(false)}
                  onClick={() => {
                    document
                      .getElementById('services')
                      ?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  Explore Our Expertise
                  <span>↗</span>
                </button>

              </div>

              {/* Trust Stats */}

              <div
                style={styles.trustRow}
                className="landing-trust-row"
              >

                <div style={styles.trustItem}>
                  <span style={styles.trustNumber}>25+</span>
                  <span style={styles.trustLabel}>
                    Years Engineering Experience
                  </span>
                </div>

                <div style={styles.divider} />

                <div style={styles.trustItem}>
                  <span style={styles.trustNumber}>06+</span>
                  <span style={styles.trustLabel}>
                    Core Engineering Disciplines
                  </span>
                </div>

                <div style={styles.divider} />

                <div style={styles.trustItem}>
                  <span style={styles.trustNumber}>360°</span>
                  <span style={styles.trustLabel}>
                    Design-to-Execution Support
                  </span>
                </div>

              </div>

            </div>

            {/* ================= RIGHT VISUAL ================= */}

            <div
              style={styles.visualArea}
              className="landing-visual"
            >

              <div
                style={styles.imageFrame}
                className="landing-image-frame"
              >

                <img
                  className="landing-image"
                  style={styles.image}
                  src="https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=1200&q=85"
                  alt="Structural engineering and construction"
                />

                <div style={styles.imageOverlay} />
                <div style={styles.technicalLines} />

              </div>

              {/* Project Status */}

              <div
                style={{
                  ...styles.floatingCard,
                  ...styles.floatingTop,
                }}
                className="landing-floating-top"
              >
                <div style={styles.floatingTitle}>
                  Engineering Focus
                </div>

                <div style={styles.floatingValue}>
                  Structural <span style={styles.floatingBlue}>+</span> Civil
                </div>
              </div>

              {/* Digital Workflow */}

              <div
                style={{
                  ...styles.floatingCard,
                  ...styles.floatingBottom,
                }}
                className="landing-floating-bottom"
              >
                <div style={styles.floatingTitle}>
                  Digital Workflow
                </div>

                <div style={styles.floatingValue}>
                  STAAD.Pro · Tekla · Civil 3D
                </div>
              </div>

              {/* Small Badge */}

              <div
                style={styles.cornerBadge}
                className="landing-corner-badge"
              >
                DESIGN • ANALYZE • DETAIL
              </div>

            </div>

          </div>

          {/* ================= ENGINEERING STRIP ================= */}

          {/* <div
            style={styles.servicesStrip}
            className="landing-services-strip"
          >

            <div
              style={styles.stripItem}
              className="landing-strip-item"
            >
              <div style={styles.stripIcon}>01</div>

              <div>
                <p style={styles.stripTitle}>
                  Structural Design
                </p>
                <p style={styles.stripText}>
                  Steel & concrete systems
                </p>
              </div>
            </div>

            <div
              style={styles.stripItem}
              className="landing-strip-item"
            >
              <div style={styles.stripIcon}>02</div>

              <div>
                <p style={styles.stripTitle}>
                  Structural Analysis
                </p>
                <p style={styles.stripText}>
                  STAAD.Pro & advanced analysis
                </p>
              </div>
            </div>

            <div
              style={styles.stripItem}
              className="landing-strip-item"
            >
              <div style={styles.stripIcon}>03</div>

              <div>
                <p style={styles.stripTitle}>
                  3D Detailing
                </p>
                <p style={styles.stripText}>
                  Tekla & fabrication workflows
                </p>
              </div>
            </div>

            <div
              style={styles.stripItem}
              className="landing-strip-item"
            >
              <div style={styles.stripIcon}>04</div>

              <div>
                <p style={styles.stripTitle}>
                  Land Development
                </p>
                <p style={styles.stripText}>
                  Survey, Civil 3D & site plans
                </p>
              </div>
            </div>

          </div> */}

          {/* ================= ENGINEERING STRIP ================= */}

          <div
            style={styles.servicesStrip}
            className="landing-services-strip"
          >

            {/* 01 - Structural Design */}
            <div
              style={styles.stripItem}
              className="landing-strip-item"
            >
              <div style={styles.stripIcon}>01</div>

              <div>
                <p style={styles.stripTitle}>
                  Structural Design
                </p>

                <p style={styles.stripText}>
                  Steel & concrete systems
                </p>
              </div>
            </div>


            {/* 02 - Structural Analysis */}
            <div
              style={styles.stripItem}
              className="landing-strip-item"
            >
              <div style={styles.stripIcon}>02</div>

              <div>
                <p style={styles.stripTitle}>
                  Structural Analysis
                </p>

                <p style={styles.stripText}>
                  STAAD.Pro & advanced analysis
                </p>
              </div>
            </div>


            {/* 03 - 3D Detailing */}
            <div
              style={styles.stripItem}
              className="landing-strip-item"
            >
              <div style={styles.stripIcon}>03</div>

              <div>
                <p style={styles.stripTitle}>
                  3D Detailing
                </p>

                <p style={styles.stripText}>
                  Tekla & fabrication workflows
                </p>
              </div>
            </div>

            {/* 04 - Land Survey */}
            <div
              style={styles.stripItem}
              className="landing-strip-item"
            >
              <div style={styles.stripIcon}>04</div>

              <div>
                <p style={styles.stripTitle}>
                  Land Survey
                </p>

                <p style={styles.stripText}>
                  Topographic & boundary surveys
                </p>
              </div>
            </div>

            {/* 05 - Land Development */}
            <div
              style={styles.stripItem}
              className="landing-strip-item"
            >
              <div style={styles.stripIcon}>05</div>

              <div>
                <p style={styles.stripTitle}>
                  Land Development
                </p>

                <p style={styles.stripText}>
                  Site planning & Civil 3D
                </p>
              </div>
            </div>




          </div>

        </div>

        {/* Scroll Indicator */}

        {/* <div
          style={styles.scrollIndicator}
          className="landing-scroll"
        >
          <div style={styles.scrollMouse}>
            <span style={styles.scrollDot} />
          </div>
          Scroll to explore
        </div> */}

      </div>
    </section>
  );
};

export default NewAbout;